import axios from 'axios';
import { useAuthStore } from '@/stores/auth-store';
import { useNetworkStore } from '@/stores/network-store';

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

let cachedElectronBaseUrl: string | null = null;

export function clearApiBaseUrlCache() {
  cachedElectronBaseUrl = null;
}

const getCloudApiUrl = async () => {
  const mode = useNetworkStore.getState().mode;

  // Modo nube: siempre la API Nest (no el servidor local de Electron)
  if (mode === 'cloud') {
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL + '/api';
    }
    // Si estamos en un navegador en producción o dominio remoto (no localhost)
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return window.location.origin + '/api';
    }
    return 'http://localhost:3001/api';
  }

  const isElectron = navigator.userAgent.toLowerCase().includes('electron');
  if (isElectron) {
    if (cachedElectronBaseUrl) return cachedElectronBaseUrl;

    let baseUrl = 'http://127.0.0.1:4000';
    if ((window as any).electronAPI && (window as any).electronAPI.getSettings) {
      try {
        const settings = await (window as any).electronAPI.getSettings();
        if (settings.role === 'SECONDARY' && settings.serverIp) {
          baseUrl = `http://${settings.serverIp}:4000`;
        }
      } catch (err) {
        console.error("Error al leer config de red en axios:", err);
      }
    }
    cachedElectronBaseUrl = `${baseUrl}/local-api`;
    return cachedElectronBaseUrl;
  }
  return '/api';
};

// Request interceptor to add auth token and dynamic baseURL
api.interceptors.request.use(
  async (config) => {
    config.baseURL = await getCloudApiUrl();

    // Support bypassing the local API to hit the cloud directly
    if (config.headers['x-bypass-local']) {
      const cloudBase = import.meta.env.VITE_API_URL || 
        (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' ? window.location.origin : 'http://localhost:3001');
      config.baseURL = cloudBase + '/api';
      delete config.headers['x-bypass-local'];
    }

    if (config.url?.includes('/health')) {
      config.baseURL = config.baseURL.replace('/api', '').replace('/local-api', '');
    }

    // Strip '/api' from url if it was hardcoded somewhere
    if (config.url?.startsWith('/api')) {
      config.url = config.url.replace('/api', '');
    }

    let token = localStorage.getItem('accessToken');
    if (token) {
      // Clean up stringified null/undefined or quotes
      token = token.replace(/^["']|["']$/g, '');
      if (token === 'null' || token === 'undefined') {
        token = null;
      }
    }
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    
    // DEBUG: send to local server to see what we are doing
    if (config.url?.includes('sync') || config.url?.includes('refresh')) {
      fetch('http://127.0.0.1:4000/local-api/debug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: config.url,
          baseURL: config.baseURL,
          hasToken: !!token,
          tokenStart: token ? token.substring(0, 10) : null,
          headers: config.headers
        })
      }).catch(() => {});
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const originalBaseUrl = originalRequest.baseURL || await getCloudApiUrl();
        const response = await axios.post(`${originalBaseUrl}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers = originalRequest.headers || {};
        if (originalRequest.headers.set) {
          originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch {
        // Token refresh failed — show global session expired modal
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        useAuthStore.getState().setSessionExpired(true);
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
